import { ArrowUpRight, ChevronDown, X, Search, ChevronsUpDown, ArrowUpDown, Star, RefreshCw } from 'lucide-react';
import { refreshSendersAndReceivers, useContactRefresh } from '../../hooks/useApiHooks';
import { useDispatch, useSelector } from 'react-redux';
import { setShowBackToDashboardButton, setSelectedSender, updateWorkflowSessionState } from '../../store/reducers';
import { selectTabById, selectActiveTabData } from '../../store/selectors/tabSelectors';
import { useState, useEffect, useMemo } from 'react';
import { useLanguage, getReviewSenderDetailsTranslation, getReviewUserDetailsTranslation, getReviewAgentsTranslation } from '../../hooks/useLanguage';
import AiIcon from '../../assets/ai-icon.png';
import { extractSuggestionsFromData } from '../../utils/suggestionUtils';

function withoutContactType(contact) {
    if (contact == null || typeof contact !== 'object') return contact;
    const { contactType: _ct, ...rest } = contact;
    return rest;
}

/** "Sender" column UI: TARIC remitente_id — IDs resolved from `contacts` (senders first, then receivers). Stored in Redux as selectedSender. */
export const ReviewSenderDetails = ({ contacts = [], navigate, tabId, declarationIndex = 0 }) => {
    const dispatch = useDispatch();
    
    // CRITICAL: Get active workflow session (source of truth for storage)
    const activeWorkflowSessionId = useSelector(state => state.workflowSessions?.activeSessionId);
    const activeWorkflowSession = useSelector(state => 
        activeWorkflowSessionId ? state.workflowSessions?.sessions?.[activeWorkflowSessionId] : null
    );
    
    // Fallback to tab-based or global for backward compatibility (UI only)
    const tabData = useSelector(state => tabId ? selectTabById(state, tabId) : selectActiveTabData(state));
    const tabSelectedSender = tabData ? tabData.selectedSender : null;

    // CRITICAL: When viewing a specific export tab, we must index into workflow arrays using the tab's declarationIndex.
    // If we don't, every tab writes to index 0 and TARIC generation will only include one declaration.
    const effectiveDeclarationIndex = (tabData && typeof tabData.declarationIndex === 'number')
        ? tabData.declarationIndex
        : declarationIndex;
    
    // Sender selection is stored in selectedSender array
    const workflowSelectedSenders = activeWorkflowSession?.state?.selectedSender || [];
    const workflowSelectedSender = workflowSelectedSenders[effectiveDeclarationIndex] || null;
    
    const globalSelectedSenders = useSelector(state => state.user.selectedSender || []);
    const globalSelectedSender = globalSelectedSenders[effectiveDeclarationIndex] || null;
    
    const selectedSenderFromStores = workflowSelectedSender || tabSelectedSender || globalSelectedSender;

    // Get extracted/editable data for suggestions (from workflow session, all declarations together)
    const workflowEditableData = activeWorkflowSession?.state?.editableData || null;
    const extractedData = useSelector(state => state.documents.extractedData);
    const editableDataFromRedux = useSelector(state => state.documents.editableData);
    // Priority: workflow session > tab > global
    const editableData = workflowEditableData || (tabData ? tabData.editableData : editableDataFromRedux);
    const declarationForIndex = useMemo(() => {
        const arr = Array.isArray(editableData)
            ? editableData
            : editableData && typeof editableData === 'object'
              ? [editableData]
              : [];
        return arr[effectiveDeclarationIndex] || null;
    }, [editableData, effectiveDeclarationIndex]);

    const sendersFromStore = useSelector((state) => state.senders ?? []);
    const receiversFromStore = useSelector((state) => state.receivers ?? []);
    const contactsList = useMemo(
        () => [
            ...sendersFromStore.map((sender) => ({ ...sender, contactType: 'sender' })),
            ...receiversFromStore.map((receiver) => ({ ...receiver, contactType: 'receiver' })),
        ],
        [sendersFromStore, receiversFromStore]
    );
    const sendersOnly = useMemo(
        () => contactsList.filter((c) => c && c.contactType === 'sender'),
        [contactsList]
    );
    const receiversOnly = useMemo(
        () => contactsList.filter((c) => c && c.contactType === 'receiver'),
        [contactsList]
    );

    const remitenteResolvedFromDeclarationId = useMemo(() => {
        if (selectedSenderFromStores) return null;
        const raw = declarationForIndex?.remitente_id;
        if (raw == null || raw === '') return null;
        const num = Number(raw);
        if (!Number.isFinite(num)) return null;
        const match =
            sendersOnly.find((c) => Number(c?.id) === num) ||
            receiversOnly.find((c) => Number(c?.id) === num) ||
            null;
        return match ? withoutContactType(match) : null;
    }, [selectedSenderFromStores, declarationForIndex, sendersOnly, receiversOnly]);

    useEffect(() => {
        if (!remitenteResolvedFromDeclarationId) return;
        const existing = workflowSelectedSenders[effectiveDeclarationIndex];
        if (existing && Number(existing.id) === Number(remitenteResolvedFromDeclarationId.id)) return;

        if (activeWorkflowSessionId) {
            const next = [...(workflowSelectedSenders || [])];
            while (next.length <= effectiveDeclarationIndex) next.push(null);
            next[effectiveDeclarationIndex] = remitenteResolvedFromDeclarationId;
            dispatch(updateWorkflowSessionState(activeWorkflowSessionId, { selectedSender: next }));
        }
        dispatch(setSelectedSender(remitenteResolvedFromDeclarationId, effectiveDeclarationIndex));
    }, [
        remitenteResolvedFromDeclarationId,
        effectiveDeclarationIndex,
        activeWorkflowSessionId,
        workflowSelectedSenders,
        dispatch,
    ]);

    const finalSelectedSender = selectedSenderFromStores || remitenteResolvedFromDeclarationId;
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContactsFilter, setModalContactsFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('id-asc');
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [isSpanish] = useLanguage();
    const { refresh: handleRefreshContacts, isRefreshing } = useContactRefresh(refreshSendersAndReceivers);

    useEffect(() => {
        if (isModalOpen) setModalContactsFilter('all');
    }, [isModalOpen]);

    // Extract suggestions from data arrays
    const dataForSuggestions = editableData || extractedData;
    const declarationIdx = effectiveDeclarationIndex;
    const suggestions = useMemo(() => {
      return extractSuggestionsFromData(
        Array.isArray(dataForSuggestions) ? dataForSuggestions : [],
        declarationIdx
      );
    }, [dataForSuggestions, declarationIdx]);
    
    const suggestedSenders = suggestions.remitentes || [];
    
    // Match suggested senders to senders list only by nombre and eori (no id matching)
    const matchedSuggestedSenders = useMemo(() => {
      const list = sendersOnly;
      return suggestedSenders
        .map(suggestion => {
          const matched = list.find((contact) => {
            const nombreMatch = contact.nombre && suggestion.nombre &&
              contact.nombre.toLowerCase().trim() === suggestion.nombre.toLowerCase().trim();
            const eoriMatch = contact.eori && suggestion.eori &&
              contact.eori.toLowerCase().trim() === suggestion.eori.toLowerCase().trim();

            return nombreMatch && eoriMatch;
          });
          return matched ? { ...matched, score: suggestion.score, reason: suggestion.reason } : null;
        })
        .filter(Boolean)
        .sort((a, b) => (b.score || 0) - (a.score || 0))
        .slice(0, 3);
    }, [suggestedSenders, sendersOnly]);

    const sortOptions = [
        { value: 'id-asc', label: getReviewSenderDetailsTranslation('idAsc', isSpanish) },
        { value: 'id-desc', label: getReviewSenderDetailsTranslation('idDesc', isSpanish) },
        { value: 'name-asc', label: getReviewSenderDetailsTranslation('nameAZ', isSpanish) },
        { value: 'name-desc', label: getReviewSenderDetailsTranslation('nameZA', isSpanish) },
        { value: 'date-desc', label: getReviewSenderDetailsTranslation('newestFirst', isSpanish) },
        { value: 'date-asc', label: getReviewSenderDetailsTranslation('oldestFirst', isSpanish) }
    ];

    const handleSenderSelect = (raw) => {
        const sender = withoutContactType(raw);
        if (activeWorkflowSessionId) {
            const currentSenders = [...(workflowSelectedSenders || [])];
            while (currentSenders.length <= effectiveDeclarationIndex) {
                currentSenders.push(null);
            }
            currentSenders[effectiveDeclarationIndex] = sender;
            dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
                selectedSender: currentSenders,
            }));
            dispatch(setSelectedSender(sender, effectiveDeclarationIndex));
        } else {
            dispatch(setSelectedSender(sender, effectiveDeclarationIndex));
        }
        setIsModalOpen(false);
        setSearchQuery('');
    };

    const modalBaseContacts = useMemo(() => {
        if (modalContactsFilter === 'sender') return sendersOnly;
        if (modalContactsFilter === 'receiver') return receiversOnly;
        return contactsList;
    }, [modalContactsFilter, sendersOnly, receiversOnly, contactsList]);

    const filteredSenders = useMemo(() => {
        const list = modalBaseContacts;
        if (!searchQuery || typeof searchQuery !== 'string') {
            return list;
        }

        const query = searchQuery.toLowerCase().trim();
        if (!query) {
            return list;
        }

        return list.filter((contact) => {
            if (!contact || typeof contact !== 'object') {
                return false;
            }
            
            // Helper function to safely convert and search a field
            const searchInField = (field) => {
                if (field == null) return false;
                if (typeof field === 'string' && field.trim() === '') return false;
                if (typeof field === 'number') {
                    return String(field).toLowerCase().includes(query);
                }
                const fieldStr = String(field).toLowerCase().trim();
                return fieldStr.length > 0 && fieldStr.includes(query);
            };
            
            // Search across multiple fields
            return searchInField(contact.nombre) ||
                   searchInField(contact.identificacion_fiscal) ||
                   searchInField(contact.nif) ||
                   searchInField(contact.eori) ||
                   searchInField(contact.direccion) ||
                   searchInField(contact.localidad) ||
                   searchInField(contact.pais);
        });
    }, [modalBaseContacts, searchQuery]);

    const sortedSenders = useMemo(() => {
        const sortKey = (c) => {
            const n = Number(c?.id);
            return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
        };
        return [...filteredSenders].sort((a, b) => {
            switch (sortBy) {
                case 'id-asc':
                    return sortKey(a) - sortKey(b);
                case 'id-desc':
                    return sortKey(b) - sortKey(a);
                case 'name-asc':
                    return (a.nombre || '').localeCompare(b.nombre || '');
                case 'name-desc':
                    return (b.nombre || '').localeCompare(a.nombre || '');
                case 'date-asc':
                    return new Date(a.date_created || 0) - new Date(b.date_created || 0);
                case 'date-desc':
                    return new Date(b.date_created || 0) - new Date(a.date_created || 0);
                default:
                    return 0;
            }
        });
    }, [filteredSenders, sortBy]);

    const getSortLabel = () => {
        return sortOptions.find(option => option.value === sortBy)?.label || getReviewSenderDetailsTranslation('sortBy', isSpanish);
    };

    return (
        <div className="text-left text-sm flex flex-col">
            <div className='flex justify-between gap-4 mb-2 items-center'>
                <h1 className='font-semibold'>{getReviewSenderDetailsTranslation('sender', isSpanish)}</h1>
            </div>
            
            <div className={`border-containera form flex flex-col ${finalSelectedSender ? "flex-1" : ""}`}>
                <div className='relative w-full' {...(!finalSelectedSender && { id: `review-sender-${declarationIndex}` })}>
                    <button className={`w-full flex gap-2 justify-between items-center border-container rounded-lg p-4 ${finalSelectedSender ? " " : ""}`} onClick={() => setIsModalOpen(true)}>
                    <div className='flex gap-2 items-center'>
                        {finalSelectedSender && (
                            <span className="text-xs bg-bgGray px-3 py-1 text-center rounded-full whitespace-nowrap h-fit">
                                {finalSelectedSender.id}
                            </span>
                        )}
                        {finalSelectedSender ? <p className='truncate'>{finalSelectedSender.nombre}</p> : <p className='opacity-75 truncate'>{getReviewSenderDetailsTranslation('selectSender', isSpanish)}</p>}
                    </div>
                        <ChevronsUpDown className="w-4 h-4" />
                    </button>
                </div>
                {finalSelectedSender && (
                    <div className='p-4 bg-gray-100 rounded-lg mt-4 flex-1' id={`review-sender-${declarationIndex}`}>
                        <div className='review-flex flex gap-4 !mt-0'>
                            <div className="form-group flex-1" id={`sender_nombre${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                                <label htmlFor="companyName">{getReviewSenderDetailsTranslation('senderName', isSpanish)}</label>
                                <p className='font-bold'>{finalSelectedSender && finalSelectedSender.nombre || "N/A"}</p>
                            </div>
                            <div className="form-group flex-1" id={`sender_nif${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                                <label htmlFor="address">{getReviewSenderDetailsTranslation('senderTaxId', isSpanish)}</label>
                                <p className='font-bold'>{finalSelectedSender && finalSelectedSender.nif || "N/A"}</p>
                            </div>
                        </div>
                        <div className='review-flex flex gap-4'>
                            <div className="form-group flex-1" id={`sender_eori${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                                <label htmlFor="taxId">{getReviewSenderDetailsTranslation('senderEori', isSpanish)}</label>
                                <p className='font-bold'>{finalSelectedSender && finalSelectedSender.eori  || "N/A"}</p>
                            </div>
                            <div className="form-group flex-1" id={`sender_direccion${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                                <label htmlFor="eori">{getReviewSenderDetailsTranslation('senderAddress', isSpanish)}</label>
                                <p className='font-bold'>{finalSelectedSender && finalSelectedSender.direccion  || "N/A"}</p>
                            </div>
                        </div>
                        <div className='review-flex flex gap-4'>
                            <div className="form-group flex-1" id={`sender_localidad${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                                <label htmlFor="city">{getReviewSenderDetailsTranslation('senderCity', isSpanish)}</label>
                                <p className='font-bold'>{finalSelectedSender && finalSelectedSender.localidad  || "N/A"}</p>
                            </div>
                            <div className="form-group flex-1" id={`sender_pais${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                                <label htmlFor="postalCode">{getReviewSenderDetailsTranslation('senderCountry', isSpanish)}</label>
                                <p className='font-bold'>{finalSelectedSender && finalSelectedSender.pais  || "N/A"}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>



             {/* Suggested Senders */}
             {!finalSelectedSender && matchedSuggestedSenders.length > 0 && (
                    <>
                    <h2 className="text-xs font-light mt-4">{getReviewSenderDetailsTranslation('suggestedSenders', isSpanish)}</h2>
                <div className="flex flex-wrap gap-2 mt-2">
                    {matchedSuggestedSenders.map((suggestedSender, index) => (
                        <button
                            key={`suggested-sender-${declarationIndex}-${suggestedSender.id || index}-${index}`}
                            onClick={() => handleSenderSelect(suggestedSender)}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-full transition-colors"
                            title={suggestedSender.reason || `Score: ${(suggestedSender.score * 100).toFixed(0)}%`}
                        >
                          <img src={AiIcon} alt="AI Icon" className="w-4 h-4" />
                            <span className="font-medium text-xs truncate">{suggestedSender.nombre}</span>
                            {suggestedSender.score && (
                                <span className="text-[8px] text-blue-400 font-semibold ">
                                    {(suggestedSender.score * 100).toFixed(0)}%
                                </span>
                              )}
                        </button>
                    ))}
                </div>
                </>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 animate-modalEnter">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">{getReviewSenderDetailsTranslation('selectSender', isSpanish)}</h2>
                            <button onClick={() => {
                                setIsModalOpen(false);
                                setSearchQuery('');
                            }} className="p-1 hover:bg-gray-100 rounded-full">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
       
                        <div className="flex gap-2 mb-4 relative z-99">
                            {/* Search Input */}
                            <div className="relative flex-1">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    className="w-full pl-10 pr-4 py-2 border-container rounded-full "
                                    placeholder={getReviewSenderDetailsTranslation('searchSenders', isSpanish)}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>

                            <button
                                type="button"
                                onClick={(e) => handleRefreshContacts(e)}
                                disabled={isRefreshing}
                                title={getReviewAgentsTranslation('refreshList', isSpanish)}
                                className="relative z-20 shrink-0 flex items-center justify-center p-2 rounded-full hover:bg-gray-100 disabled:opacity-50"
                                aria-label={getReviewAgentsTranslation('refreshList', isSpanish)}
                            >
                                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                            </button>

                            {/* Custom Sort Dropdown */}
                            <div className="relative button-no-hover-effect">
                                <button
                                    className="flex items-center gap-2 px-2 pr-0 py-2 rounded-full"
                                    onClick={() => setIsSortOpen(!isSortOpen)}
                                >
                                    <ArrowUpDown className="w-4 h-4" />
                                    <span>{getSortLabel()}</span>
                                    <ChevronDown className={`w-4 h-4 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {isSortOpen && (
                                    <div className="overflow-hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-container py-1 z-10">
                                        {sortOptions.map((option) => (
                                            <button
                                                key={option.value}
                                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                                                    sortBy === option.value ? 'text-primary' : 'text-gray-700'
                                                }`}
                                                onClick={() => {
                                                    setSortBy(option.value);
                                                    setIsSortOpen(false);
                                                }}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2 mb-3">
                            {[
                                { id: 'all', label: getReviewUserDetailsTranslation('contactsFilterAll', isSpanish) },
                                { id: 'sender', label: getReviewUserDetailsTranslation('contactsFilterSenders', isSpanish) },
                                { id: 'receiver', label: getReviewUserDetailsTranslation('contactsFilterReceivers', isSpanish) },
                            ].map(({ id, label }) => (
                                <button
                                    key={id}
                                    type="button"
                                    className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
                                        modalContactsFilter === id
                                            ? 'bg-default text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                    onClick={() => setModalContactsFilter(id)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        <div className="max-h-[60vh] min-h-[60vh] overflow-y-auto">
                            {contactsList.length > 0 ? (
                                <div className="space-y-0">
                                    {sortedSenders.length > 0 ? (
                                        sortedSenders.map((contact, index) => (
                                            <div 
                                                key={`sender-${declarationIndex}-${contact.id || index}-${index}`} 
                                                className='p-3 px-0 hover:bg-gray-100 cursor-pointer border-bottom-gray'
                                                onClick={() => handleSenderSelect(contact)}
                                            >
                                                <div className="flex justify-between items-start gap-2">
                                                    <div className='flex gap-2 flex-1'>
                                                    <span className="text-xs bg-bgGray px-3 py-1 text-center rounded-full whitespace-nowrap h-fit min-w-[2.5rem]">
                                                            {contact.id}
                                                        </span>
                                                        <div className='flex flex-col flex-1 w-full'>
                                                        <div className='flex justify-between flex-1'>
                                                        <p className="font-medium flex flex-wrap items-center gap-1">
                                                            {contact.nombre}
                                                        </p>
                                                        {modalContactsFilter === 'all' && contact.contactType && (
                                                                <span className="text-[10px] font-normal text-gray-700">
                                                                    {contact.contactType === 'sender'
                                                                        ? getReviewUserDetailsTranslation('contactsBadgeSender', isSpanish)
                                                                        : getReviewUserDetailsTranslation('contactsBadgeReceiver', isSpanish)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className='flex justify-between flex-1'>
                                                        <p className="text-sm text-gray-500">
                                                            {contact.pais}
                                                        </p>
                                                        {(contact.nif) && (
                                                        <span className="text-[10px] text-gray-700 ">
                                                            {contact.nif && `${getReviewUserDetailsTranslation('nif', isSpanish)}: ${contact.nif} `}
                                                        </span>
                                                    )}
                                                        </div>
                                                        </div>
                                                    </div>
                                                    
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-4 text-gray-500">
                                            {searchQuery.trim()
                                                ? `${getReviewSenderDetailsTranslation('noSendersFound', isSpanish)} "${searchQuery}"`
                                                : getReviewUserDetailsTranslation('noContactsForModalFilter', isSpanish)}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <p className='opacity-75 mb-4'>{getReviewSenderDetailsTranslation('noSendersAvailable', isSpanish)}</p>
                                    <div className="flex flex-wrap gap-2 items-center justify-center">
                                        <button
                                            type="button"
                                            onClick={(e) => handleRefreshContacts(e)}
                                            disabled={isRefreshing}
                                            className="svg-force-white flex gap-2 items-center justify-center px-4 py-2 bg-default text-white rounded-full disabled:opacity-50"
                                        >
                                            <RefreshCw className={`w-5 h-5 stroke-white ${isRefreshing ? 'animate-spin' : ''}`} />
                                            <span className="text-white">{getReviewAgentsTranslation('refreshList', isSpanish)}</span>
                                        </button>
                                        <button
                                            type="button"
                                            className="flex gap-2 items-center justify-center px-4 py-2 rounded-full border border-gray-300 text-gray-800 hover:bg-gray-100"
                                            onClick={() => {
                                                setIsModalOpen(false);
                                                dispatch(setShowBackToDashboardButton(true));
                                                navigate("/sender-management");
                                            }}
                                        >
                                            <span>{getReviewSenderDetailsTranslation('addSender', isSpanish)}</span>
                                            <ArrowUpRight className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}